import React, {useState, useEffect} from 'react';
import {hooks} from '../hooks';
import {svg} from '../assets/svg';
import {components} from '../components';
import { axiosInstance } from '../api/api';

interface UserData {
  id: number;
  user_id: number;
  employee_personal: {
    first_name: string;
    middle_name: string | null;
    last_name: string | null;
    full_name: string;
    mobile_phone: string | null;
    address: string | null;
    profile_picture_url: string | null;
  };
  user: {
    email: string;
    name: string;
  };
}

export const EditProfile: React.FC = () => {
  const navigate = hooks.useNavigate();
  const dispatch = hooks.useDispatch();

  const [opacity, setOpacity] = useState<number>(0);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);

        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          console.log('No auth token found');
          return;
        }

        const sessionCredentials = sessionStorage.getItem('dpp_temp_credentials');
        const loginUsername = sessionCredentials ? JSON.parse(sessionCredentials).name : null;

        if (loginUsername) {
          try {
            const response = await axiosInstance.get("/api/ap/v1/employee", {
              params: { 
                search: loginUsername,
                per_page: 10
              }
            });

            if (response.data && response.data.data && response.data.data.length > 0) {
              const employeeData = response.data.data[0];
              setUserData(employeeData);
              localStorage.setItem('userData', JSON.stringify(employeeData));
              return;
            }
          } catch (searchError) {
            console.log('Specific search failed');
          }
        }
        const response = await axiosInstance.get("/api/ap/v1/employee", {
          params: { 
            page: 1, 
            per_page: 100
          }
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
          const employeeData = response.data.data[0];
          setUserData(employeeData);
          localStorage.setItem('userData', JSON.stringify(employeeData));
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  const getUserName = (): string => {
    if (!userData) return 'User';
    return userData.employee_personal?.full_name || 
           userData.user?.name || 
           'User';
  };

  const getUserEmail = (): string => {
    if (!userData) return 'user@example.com';
    return userData.user?.email || 'user@example.com';
  };

  const getUserPhone = (): string => {
    if (!userData) return '-';
    return userData.employee_personal?.mobile_phone || '-';
  };

  const getUserAddress = (): string => {
    if (!userData) return '-';
    return userData.employee_personal?.address || '-';
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title='Data Pribadi'
        showGoBack={true}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <main className='scrollable container'>
          <section
            style={{
              backgroundColor: 'var(--white-color)',
              paddingLeft: 20,
              paddingRight: 20,
              borderRadius: 10,
              paddingTop: 50,
              paddingBottom: 30,
              marginTop: 10,
              textAlign: 'center',
            }}
          >
            <div style={{
              fontSize: '16px',
              fontWeight: 500,
              color: '#64748B',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            }}>Loading...</div>
          </section>
        </main>
      );
    }

    return (
      <main className='scrollable container'>
        <section
          style={{
            backgroundColor: 'var(--white-color)',
            paddingLeft: 20,
            paddingRight: 20,
            borderRadius: 10,
            paddingTop: 50,
            paddingBottom: 30,
            marginTop: 10,
          }}
        >
          <div
            style={{

              maxWidth: 100,
              marginLeft: 'auto',
              marginRight: 'auto',
              marginBottom: 40,
            }}
            className='center'
          >

            <div
              style={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: '#E6F3F8',
                // display: 'flex',
                // alignItems: 'center',
                // justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                padding: 20
              }}
            >
              <div style={{ 
                transform: 'scale(4) translateY(2px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg.UserNoRectSvg />
              </div>
            </div>
          </div>
          
          {/* Name Field */}
          <div style={{marginBottom: 24}}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8,
              marginLeft: 4,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              Nama Lengkap
            </div>
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #F1F5F9',
              borderRadius: '12px',
              fontSize: '16px',
              color: '#1E293B',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 500,
              lineHeight: '1.5',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            }}>
              {getUserName()}
            </div>
          </div>
          
          {/* Email Field */}
          <div style={{marginBottom: 24}}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8,
              marginLeft: 4,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              Email
            </div>
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #F1F5F9',
              borderRadius: '12px',
              fontSize: '16px',
              color: '#1E293B',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 500,
              lineHeight: '1.5',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            }}>
              {getUserEmail()}
            </div>
          </div>
          
          {/* Phone Field */}
          <div style={{marginBottom: 24}}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8,
              marginLeft: 4,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              No. Telepon
            </div>
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #F1F5F9',
              borderRadius: '12px',
              fontSize: '16px',
              color: '#1E293B',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 500,
              lineHeight: '1.5',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            }}>
              {getUserPhone()}
            </div>
          </div>
          
          {/* Address Field */}
          <div style={{marginBottom: 20}}>
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: 8,
              marginLeft: 4,
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              Alamat
            </div>
            <div style={{
              padding: '16px 20px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #F1F5F9',
              borderRadius: '12px',
              fontSize: '16px',
              color: '#1E293B',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 500,
              lineHeight: '1.5',
              minHeight: '60px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
            }}>
              {getUserAddress()}
            </div>
          </div>
        </section>
      </main>
    );
  };

  return (
    <div
      id='screen'
      style={{opacity}}
    >
      {renderHeader()}
      {renderContent()}
    </div>
  );
};